import { useState } from "react";
import { Loader2, Search, Sparkles, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { capture } from "@/lib/posthog";
import {
  useStructureProducts,
  type SuggestionMessage,
} from "@/hooks/mutations/useStructureProducts";
import type { ProductSegment, StructuredProduct } from "@/types/domain";
import {
  ActionBar,
  ActionButton,
  Chip,
  Eyebrow,
  InlineError,
  ScreenLead,
  ScreenTitle,
  type Tone,
} from "./OnboardingShell";
import {
  MAX_PRODUCTS,
  normalizeText,
  type StarterChip,
} from "./lineHelpers";

// Una frase corta por turno, el mismo tope que el backend.
const MAX_INPUT_CHARS = 100;

const TONE_CLASSES: Record<Tone, { input: string; icon: string; button: string }> = {
  emerald: {
    input: "border-emerald-200 focus-within:border-emerald-400",
    icon: "text-emerald-600",
    button: "bg-emerald-600 hover:bg-emerald-700",
  },
  blue: {
    input: "border-blue-200 focus-within:border-blue-400",
    icon: "text-blue-700",
    button: "bg-blue-700 hover:bg-blue-800",
  },
};

/**
 * Pregunta 1 de cada flujo: qué vende el proveedor o qué obras hace el
 * contratista. Los chips agregan términos curados sin IA; el texto libre lo
 * ordena la IA, que no agrega sinónimos pero sí abre las categorías.
 */
export function ProductStep({
  segment,
  tone,
  eyebrow,
  title,
  lead,
  placeholder,
  starters,
  termsLabel,
  products,
  onAddProducts,
  onRemoveProducts,
  onNext,
  onBack,
  onEscape,
}: {
  segment: ProductSegment;
  tone: Tone;
  eyebrow: string;
  title: string;
  lead: string;
  placeholder: string;
  starters: StarterChip[];
  /** El rótulo sobre los términos: dónde los va a buscar la IA. */
  termsLabel: string;
  products: StructuredProduct[];
  /** `name` propone el nombre de la línea: el de la IA, o el del chip. */
  onAddProducts: (
    products: StructuredProduct[],
    name?: { value: string; fromAi: boolean },
  ) => void;
  onRemoveProducts: (terms: string[]) => void;
  onNext: () => void;
  onBack: () => void;
  /** Salida a la app sin configurar: solo se ofrece si la IA falla. */
  onEscape: () => void;
}) {
  const structure = useStructureProducts();
  const [input, setInput] = useState("");
  const [history, setHistory] = useState<SuggestionMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [escapeShown, setEscapeShown] = useState(false);
  const toneClasses = TONE_CLASSES[tone];

  const question =
    history.at(-1)?.role === "assistant" ? history.at(-1)!.content : null;
  const full = products.length >= MAX_PRODUCTS;
  const hasTerm = (term: string) =>
    products.some((p) => normalizeText(p.term) === normalizeText(term));

  function submit() {
    const text = input.trim();
    if (!text || structure.isPending) return;
    const nextHistory: SuggestionMessage[] = [...history, { role: "user", content: text }];
    setError(null);
    capture("onboarding_ai_requested", { segment, turn: nextHistory.length });

    structure.mutate(
      { messages: nextHistory, segment },
      {
        onSuccess: (result) => {
          if (result.needsMoreInfo) {
            capture("onboarding_ai_question", { segment });
            setHistory([...nextHistory, { role: "assistant", content: result.question }]);
            setInput("");
            return;
          }
          setHistory([]);
          if (result.products.length === 0) {
            setError("No encontramos nada que buscar en lo que escribiste. Prueba con otras palabras.");
            return;
          }
          onAddProducts(result.products, { value: result.profileName, fromAi: true });
          setInput("");
        },
        onError: () => {
          setError("No pudimos leer lo que escribiste. Inténtalo de nuevo.");
          setEscapeShown(true);
        },
      },
    );
  }

  function toggleStarter(starter: StarterChip) {
    const selected = starter.products.every((p) => hasTerm(p.term));
    if (selected) {
      onRemoveProducts(starter.products.map((p) => p.term));
    } else {
      onAddProducts(starter.products, { value: starter.label, fromAi: false });
    }
  }

  return (
    <div>
      <Eyebrow tone={tone}>{eyebrow}</Eyebrow>
      <ScreenTitle>{title}</ScreenTitle>
      <ScreenLead>{lead}</ScreenLead>

      {question && (
        <p className="mt-5 max-w-[620px] rounded-[14px] bg-white px-4 py-3 text-[14.5px] font-semibold leading-[1.5] text-slate-800 shadow-sm">
          {question}
        </p>
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
        className={cn(
          "mt-5 flex max-w-[620px] items-center gap-2 rounded-[18px] border-2 bg-white py-2 pl-4 pr-2",
          toneClasses.input,
        )}
      >
        <Search className={cn("size-4.5 shrink-0", toneClasses.icon)} strokeWidth={2.4} />
        <input
          value={input}
          onChange={(event) => setInput(event.target.value.slice(0, MAX_INPUT_CHARS))}
          disabled={full}
          placeholder={
            full
              ? `Máximo ${MAX_PRODUCTS} términos por línea`
              : question
                ? "Tu respuesta"
                : placeholder
          }
          className="min-w-0 flex-1 bg-transparent py-2 text-[17px] font-semibold text-slate-900 outline-none placeholder:font-normal placeholder:text-slate-400 disabled:cursor-not-allowed"
        />
        <button
          type="submit"
          disabled={!input.trim() || structure.isPending || full}
          className={cn(
            "inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-[12px] px-4 py-2.5 text-[14px] font-bold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40",
            toneClasses.button,
          )}
        >
          {structure.isPending && <Loader2 className="size-4 animate-spin" />}
          Agregar
        </button>
      </form>

      <div className="mt-4 flex max-w-[640px] flex-wrap gap-2">
        {starters.map((starter) => (
          <Chip
            key={starter.label}
            tone={tone}
            selected={starter.products.every((p) => hasTerm(p.term))}
            onClick={() => toggleStarter(starter)}
          >
            {starter.label}
          </Chip>
        ))}
      </div>

      {error && (
        <InlineError message={error} onEscape={escapeShown ? onEscape : undefined} />
      )}

      {products.length > 0 && (
        <div className="mt-6">
          <p className="flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-violet-600">
            <Sparkles className="size-3.5" />
            {termsLabel}
          </p>
          <div className="mt-2.5 flex max-w-[640px] flex-wrap gap-2">
            {products.map((product) => (
              <span
                key={product.term}
                className="inline-flex items-center gap-1.5 rounded-lg bg-violet-100 py-1.5 pl-3 pr-1.5 text-[13.5px] font-semibold text-violet-900"
              >
                {product.label}
                <button
                  type="button"
                  onClick={() => onRemoveProducts([product.term])}
                  aria-label={`Quitar ${product.label}`}
                  className="flex size-5 cursor-pointer items-center justify-center rounded-md text-violet-500 hover:bg-violet-200 hover:text-violet-900"
                >
                  <X className="size-3.5" strokeWidth={2.6} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <ActionBar onBack={onBack}>
        <ActionButton
          tone={tone}
          onClick={() => {
            capture("onboarding_ai_applied", { segment, products: products.length });
            onNext();
          }}
          disabled={products.length === 0}
          arrow
        >
          Continuar
        </ActionButton>
      </ActionBar>
    </div>
  );
}
